import * as z from "zod";
import type { SupportV2MigrationStep } from "./migration-execution";

export const MIGRATION_RUN_STATUS_VALUES = [
  "pending",
  "running",
  "completed",
  "completed_with_failures",
  "failed",
  "cancelled",
] as const;

export const MigrationRunStatus = z.enum(MIGRATION_RUN_STATUS_VALUES);
export type MigrationRunStatus = z.infer<typeof MigrationRunStatus>;

export const MIGRATION_STEP_STATUS_VALUES = [
  "pending",
  "running",
  "completed",
  "completed_with_failures",
  "failed",
  "skipped",
] as const;

export const MigrationStepStatus = z.enum(MIGRATION_STEP_STATUS_VALUES);
export type MigrationStepStatus = z.infer<typeof MigrationStepStatus>;

export const MIGRATION_PHASE_VALUES = [
  "INTEGRATIONS",
  "TAGS",
  "CONVERSATIONS",
  "CONVERSATION_TIMELINE_BACKFILL",
  "SETTINGS_BACKFILL",
  "VIEWS_AND_FOLDERS",
  "MACROS",
  "RULES_V2_TO_V3",
] as const;

export const MigrationPhase = z.enum(MIGRATION_PHASE_VALUES);
export type MigrationPhase = z.infer<typeof MigrationPhase>;

export const DURABLE_MIGRATION_PHASES_BY_STEP = {
  INTEGRATIONS: [],
  TAGS: [],
  CONVERSATIONS: ["CONVERSATIONS", "CONVERSATION_TIMELINE_BACKFILL"],
  SETTINGS_BACKFILL: [],
  VIEWS_AND_FOLDERS: [],
  MACROS: ["MACROS"],
  RULES_V2_TO_V3: [],
} as const satisfies Record<SupportV2MigrationStep, readonly MigrationPhase[]>;

export function getDurableMigrationPhasesForSteps(
  steps: Iterable<SupportV2MigrationStep>,
): readonly MigrationPhase[] {
  const phases = new Set<MigrationPhase>();
  for (const step of steps) {
    for (const phase of DURABLE_MIGRATION_PHASES_BY_STEP[step]) {
      phases.add(phase);
    }
  }
  return MIGRATION_PHASE_VALUES.filter((phase) => phases.has(phase));
}

const MongoObjectIdHex = z.string().regex(/^[a-f0-9]{24}$/);
const IsoDateTimeString = z
  .string()
  .refine((value) => Number.isFinite(Date.parse(value)), {
    message: "Invalid date-time string",
  });

export const MongoObjectIdScanCheckpoint = z.object({
  upperBoundId: MongoObjectIdHex.optional(),
  upperBoundCreatedAt: IsoDateTimeString.optional(),
  lastProcessedId: MongoObjectIdHex.optional(),
  lastProcessedCreatedAt: IsoDateTimeString.optional(),
  processed: z.number().int().nonnegative(),
});
export type MongoObjectIdScanCheckpoint = z.infer<
  typeof MongoObjectIdScanCheckpoint
>;

export const CrdbLegacyIdScanCheckpoint = z.object({
  upperBoundLegacyId: z.string().optional(),
  lastProcessedLegacyId: z.string().optional(),
  processed: z.number().int().nonnegative(),
});
export type CrdbLegacyIdScanCheckpoint = z.infer<
  typeof CrdbLegacyIdScanCheckpoint
>;

export interface MigrationStepProgressSnapshot {
  readonly startedAt?: Date | null;
  readonly completedAt?: Date | null;
  readonly skipped?: boolean;
  readonly failed: number;
  readonly lastError?: string | null;
}

export function deriveMigrationStepStatus(
  snapshot: MigrationStepProgressSnapshot,
): MigrationStepStatus {
  if (snapshot.lastError) {
    return "failed";
  }
  if (snapshot.skipped) {
    return "skipped";
  }
  if (!snapshot.startedAt) {
    return "pending";
  }
  if (!snapshot.completedAt) {
    return "running";
  }
  if (snapshot.failed > 0) {
    return "completed_with_failures";
  }
  return "completed";
}

export function deriveMigrationRunStatus(
  stepStatuses: Iterable<MigrationStepStatus>,
): MigrationRunStatus {
  const statuses = [...stepStatuses];
  if (statuses.every((s) => s === "pending")) {
    return "pending";
  }
  if (statuses.includes("failed")) {
    return "failed";
  }
  if (statuses.some((s) => s === "running" || s === "pending")) {
    return "running";
  }
  if (statuses.includes("completed_with_failures")) {
    return "completed_with_failures";
  }
  return "completed";
}
